// One asset = one tile (DESIGN D6 "Tile"; DESIGN-R2 B1, B5, B6, B13, B16). The face carries ONLY: the type (small), the
// name, the env badge (PROD / DEV / GLOBAL), the state as colour + pattern + word glyph, line 1 and line 2, and the state
// markers (housekeeping, downstream). Dataflow: line 1 = the schedule word, line 2 = "moved: <word>". Control plane:
// line 1 = the state word, line 2 = the recorded summary with its tag and date ("measured 2026-09-12" /
// "INHERITED 2026-09-10" - a recorded measurement, not live health; the tile's state still comes only from the checks
// that landed on it). No visible reasons: each face line carries its reason as a title and an accessible description.
// A housekeeping asset measured BAD is never drawn solid red (only counted problems are): red outline, muted hatch and
// a clear HOUSEKEEPING tag. A set with members carries a collapsed <details> "N members" OUTSIDE the button. The button
// opens the asset's detail panel.
import { Fragment } from "react";
import type { HealthGridAsset } from "./types";
import { DRAWER_ID, usePanel } from "./AssetPanel";
import { LineWord, StateWord } from "./Tiles";
import { SHORT_WORD, anchorId, assetLook, clip, labelParts, lineReason, plural, summaryTag } from "./present";

/** A control-plane summary on a tile face is clipped at this length; the whole text is in the title and the panel. */
export const SUMMARY_FACE_CLIP = 110;

/** A name that breaks only after "_" or at a camel-case hump (a <wbr> between the pieces). */
export function AssetName({ label }: { label: string }) {
  return (
    <span className="ph-asset-name">
      {labelParts(label).map((p, i) => (
        <Fragment key={i}>
          {i > 0 ? <wbr /> : null}
          {p}
        </Fragment>
      ))}
    </span>
  );
}

export function AssetTile({ a }: { a: HealthGridAsset }) {
  const lk = assetLook(a);
  const p = usePanel();
  const hk = a.section === "housekeeping";
  const hkProblem = hk && lk.kind === "problem";
  const control = a.plane === "control";
  const downstream = a.role === "consequent";
  const base = anchorId(a.id);
  const tag = a.summary ? summaryTag(a.summary.tag, a.summary.at) : null;
  const why1 = control ? (lk.contradiction ?? (a.reason?.trim() || null)) : lineReason(a.lines.schedule);
  const why2 = control
    ? a.summary && tag
      ? `${tag.text}: ${a.summary.text} - a recorded measurement, not live health`
      : null
    : lineReason(a.lines.moved);
  const id1 = `${base}-why-1`;
  const id2 = `${base}-why-2`;
  const described = [why1 ? id1 : null, why2 ? id2 : null].filter(Boolean).join(" ");
  return (
    <div
      className={`ph-asset ph-k-${lk.kind}${hkProblem ? " ph-k-problem-hk" : ""}${hk ? " ph-asset-hk" : ""}${a.hidden ? " ph-retired" : ""}`}
      data-asset-id={a.id}
      data-testid="asset-tile"
      data-kind={lk.kind}
      data-state={a.state}
      data-section={a.section}
      data-role={a.role}
    >
      <button
        type="button"
        className="ph-asset-btn"
        aria-controls={DRAWER_ID}
        aria-expanded={p.openId === a.id}
        aria-describedby={described || undefined}
        data-open-asset={a.id}
        onClick={e => p.open(a.id, e.currentTarget)}
      >
        <span className="ph-asset-top">
          <span className="ph-asset-type">{a.type}</span>
          <span className="ph-env" data-env={a.env} data-testid="env-badge">
            {a.env}
          </span>
        </span>
        <AssetName label={a.label} />
        {control ? (
          <>
            <span className="ph-asset-line" data-line="1" title={why1 ?? undefined} aria-describedby={why1 ? id1 : undefined}>
              <StateWord lk={lk} word={SHORT_WORD[lk.kind]} />
            </span>
            {a.summary && tag ? (
              <span className="ph-asset-line ph-asset-summary" data-line="2" title={why2 ?? undefined} aria-describedby={why2 ? id2 : undefined}>
                {clip(a.summary.text, SUMMARY_FACE_CLIP)}{" "}
                <span className={`ph-recorded ph-recorded-${tag.kind}`} data-tag={tag.kind} data-testid="summary-tag">
                  {tag.text}
                </span>
              </span>
            ) : null}
          </>
        ) : (
          <>
            <span className="ph-asset-state">
              <StateWord lk={lk} word={SHORT_WORD[lk.kind]} />
            </span>
            <span className="ph-asset-line" data-line="1" title={why1 ?? undefined} aria-describedby={why1 ? id1 : undefined}>
              <LineWord l={a.lines.schedule} />
            </span>
            <span className="ph-asset-line" data-line="2" title={why2 ?? undefined} aria-describedby={why2 ? id2 : undefined}>
              moved: <LineWord l={a.lines.moved} />
            </span>
          </>
        )}
        {hk || downstream ? (
          <span className="ph-markers">
            {hk ? <span className="ph-hk-marker">housekeeping</span> : null}
            {downstream ? (
              <span className="ph-down-marker" data-testid="downstream-marker">
                downstream
              </span>
            ) : null}
          </span>
        ) : null}
      </button>
      {why1 ? (
        <span hidden id={id1} data-testid="line-why">
          {why1}
        </span>
      ) : null}
      {why2 ? (
        <span hidden id={id2} data-testid="line-why">
          {why2}
        </span>
      ) : null}
      {a.members.length > 0 ? (
        <details className="ph-members" data-testid="asset-members">
          <summary>{plural(a.members.length, "member")}</summary>
          <ul>
            {a.members.map(m => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
